import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  inject,
  Injectable,
  signal
} from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { SwUpdate } from "@angular/service-worker";
import Swal from "sweetalert2";
import semverDiff from "semver-diff";
import { ProductService } from "src/app/services/product.service";
import {
  getCurrentSoftwareProduct,
  getCurrentSoftwareProductVersion,
  SoftwareProduct
} from "src/app/support/product";
import { MemberStorageService } from "src/app/services/member-storage.service";
import { ChangelogHistoryService } from "src/app/services/changelog-history.service";
import {
  TaskExecutionPlan,
  TasksService,
  TaskType
} from "src/app/services/tasks.service";
import { sntLog } from "src/app/common/logger";
import { countFlowAction } from "src/app/support/sentry-metrics";

const logger = sntLog("UpdateService");
// @ts-ignore
import { el } from "src/app/support/dom";
import { environment } from "src/environments/environment";
import { Mutex } from "async-mutex";
import type { ChangelogComponent } from "src/app/components/ui/changelog/changelog.component";

export enum UpdateServiceStatus {
  DISABLED = "disabled",
  STANDBY = "standby",
  CHECK = "check",
  UPDATING = "updating",
  UPDATE_READY = "update-ready",
  UPDATE_ERROR = "update-error"
}

export type UpdateEntryPoint = "user_bar" | "navigation_bar" | "hamburger_menu";

@Injectable()
export class UpdateService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly memberStorageService = inject(MemberStorageService);
  private readonly tasksService = inject(TasksService);
  private readonly changelogHistoryService = inject(ChangelogHistoryService);

  private mutex = new Mutex();

  productService = inject(ProductService);

  updating$ = new BehaviorSubject<UpdateServiceStatus>(
    UpdateServiceStatus.DISABLED
  );

  // Once a new version is ready it stays ready until the page is reloaded.
  readonly updateReady = signal<boolean>(false);

  constructor() {
    this.tasksService.schedule({
      plan: TaskExecutionPlan.WHEN_STABLE,
      type: TaskType.RUN_ONCE,
      fn: () => {
        this.updating$.subscribe(async (updateStatus: UpdateServiceStatus) => {
          logger.debug(`[UpdateService] status "${updateStatus}"`);

          switch (updateStatus) {
            case UpdateServiceStatus.CHECK: {
              if (this.updateReady()) {
                break;
              }

              logger.debug(`[UpdateService] checkForUpdateResult called`);

              await this.mutex.runExclusive(async () => {
                await this.swUpdate.checkForUpdate().then((result) => {
                  logger.debug(
                    `[UpdateService] checkForUpdateResult result ${result}`
                  );

                  if (result) {
                    this.setStatus(UpdateServiceStatus.UPDATE_READY);
                  }
                });
              });

              break;
            }
          }
        });
      }
    });
  }

  applyUpdate(entryPoint: UpdateEntryPoint): void {
    if (!this.updateReady()) {
      return;
    }

    countFlowAction("app", "update", "apply", "attempted", {
      entry_point: entryPoint
    });

    window.location.reload();
  }

  private setStatus(status: UpdateServiceStatus): void {
    if (this.updateReady()) {
      return;
    }

    if (status === UpdateServiceStatus.UPDATE_READY) {
      this.updateReady.set(true);
      countFlowAction("app", "update", "shown");
    }

    this.updating$.next(status);
  }

  async enableAutomaticUpdates(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      logger.debug(`[UpdateService] DISABLED`);
      return;
    }

    this.setStatus(UpdateServiceStatus.STANDBY);

    this.swUpdate.versionUpdates.subscribe(async (updateEvent) => {
      console.groupCollapsed(`[UpdateService] ${updateEvent.type}`);
      logger.dir(updateEvent);
      console.groupEnd();

      switch (updateEvent.type) {
        case "VERSION_DETECTED": {
          this.setStatus(UpdateServiceStatus.UPDATING);
          break;
        }
        case "NO_NEW_VERSION_DETECTED": {
          this.setStatus(UpdateServiceStatus.STANDBY);
          break;
        }
        case "VERSION_INSTALLATION_FAILED": {
          this.setStatus(UpdateServiceStatus.UPDATE_ERROR);
          break;
        }
        case "VERSION_READY": {
          this.setStatus(UpdateServiceStatus.UPDATE_READY);
          break;
        }
      }
    });

    this.tasksService.schedule({
      plan: TaskExecutionPlan.WHEN_STABLE,
      type: TaskType.RECURRING,
      interval: environment.snoots.service_worker.check_update_interval,
      fn: () => {
        this.setStatus(UpdateServiceStatus.CHECK);
      }
    });
  }

  check(): void {
    this.setStatus(UpdateServiceStatus.CHECK);
  }

  async checkIfLoadedNewVersion(): Promise<void> {
    const currentVersion: string = getCurrentSoftwareProductVersion();

    const lastVersionInstalled = this.memberStorageService.getItem(
      "last-loaded-version"
    );

    if (
      !lastVersionInstalled ||
      semverDiff(lastVersionInstalled || "", currentVersion)
    ) {
      await this.showNewVersionInfo(currentVersion);
      return;
    }
  }

  private async showNewVersionInfo(version: string): Promise<void> {
    let componentRef: ComponentRef<ChangelogComponent> | null = null;
    let dismissSub: { unsubscribe(): void } | undefined;

    try {
      const componentType =
        await import("../components/ui/changelog/changelog.component").then(
          (m) => m.ChangelogComponent
        );

      const hostElement = el("div");

      componentRef = createComponent(componentType, {
        environmentInjector: this.applicationRef.injector,
        hostElement
      });

      const changelogEntry =
        this.changelogHistoryService.getLastChangelogByProduct(
          getCurrentSoftwareProduct()
        );

      if (changelogEntry?.advanced) {
        componentRef.setInput("template", "advanced");
      }

      dismissSub = componentRef.instance.dismiss.subscribe(() => {
        Swal.close();
      });

      this.applicationRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();

      await Swal.fire({
        title: undefined,
        html: hostElement,
        position: "bottom-right",
        toast: true,
        showCancelButton: false,
        showConfirmButton: false,
        // Toast + no buttons => Swal closes on any popup click; close UI is in the component.
        showCloseButton: true,
        width: 520,
        heightAuto: true,
        customClass: {
          container: ["m-0"],
          htmlContainer: ["m-0"],
          popup: ["p-0"],
          closeButton: ["swal-changelog-hide-native-close"]
        }
      });

      this.memberStorageService.setItem("last-loaded-version", version);
    } catch (ex) {
      logger.error(ex);
    } finally {
      dismissSub?.unsubscribe();

      if (componentRef) {
        this.applicationRef.detachView(componentRef.hostView);
        componentRef.destroy();
      }
    }
  }
}
